import React, { useEffect, useState } from "react";
import { t } from "i18next";
import { Dropdown } from "@fluentui/react";
import { USERTYPES } from "../../../constants/Constants";
import { getAiDraftModels } from "../../../service/AiDraftService";

/**
 * The AI draft models offered to this user ({ models, system_default, ... }), or null when the
 * setting must stay hidden: not a radiologist, AI draft not in use (the endpoint answers 403), or
 * the model is locked (DeepTek LLM: nothing to choose, and a saved choice is ignored).
 */
export const useAiDraftModelOptions = (role) => {
  const [options, setOptions] = useState(null);
  useEffect(() => {
    if (role !== USERTYPES.RADIOLOGIST) return;
    getAiDraftModels().then((response) => {
      const data = response?.status === 200 ? response.data?.data : null;
      if (data?.models?.length && !data.locked) setOptions(data);
    });
  }, [role]);
  return options;
};

/** The value to show and save: the saved model while it is still offered, else "" (system default). */
export const aiDraftPreferenceValue = (options, saved) =>
  saved && options?.models?.some((m) => m.id === saved) ? saved : "";

/** "AI draft model" row of the preferences page. onChange receives a model id, or null for the system default. */
const AiDraftModelPreference = ({ options, saved, onChange, themeName }) => {
  if (!options) return null;
  const value = aiDraftPreferenceValue(options, saved);
  const systemName = options.models.find((m) => m.id === options.system_default)?.name || options.system_default;
  return (
    <div className="ms-Grid-row single-line-form-field" data-testid="ai-draft-model-preference">
      <div className="ms-Grid-col ms-sm12 ms-lg8 single-line-form-field-value">
        <div style={{ display: "flex", alignItems: "center" }}>
          <p className="single-line-form-field-key" style={{ width: "250px" }}>
            {t("account-and-settings.ai-draft-model", "AI draft model")}
          </p>
          <Dropdown
            ariaLabel="AI draft model"
            selectedKey={value}
            options={[
              { key: "", text: `System default (${systemName})` },
              ...options.models.map((m) => ({ key: m.id, text: m.name })),
            ]}
            onChange={(_, option) => onChange(option?.key ? String(option.key) : null)}
            styles={{ root: { minWidth: 260 } }}
          />
        </div>
        {saved && !value && (
          <p style={{ fontSize: "12px", color: "#f5a524", marginLeft: "0px" }} data-testid="ai-draft-model-unavailable">
            Your previous choice is no longer available
          </p>
        )}
        <p style={{ fontSize: "13px", color: themeName === "light" ? "#808080" : "#bebebe", marginLeft: "0px" }}>
          The model the AI Draft panel starts with. You can still pick another model each time you generate.
        </p>
      </div>
    </div>
  );
};

export default AiDraftModelPreference;
